import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, ExternalLink, PlayCircle, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const FUNNEL_ORDER = ["recommended", "enrolled", "in_progress", "completed", "certified"];
const STATUS_OPTIONS = ["recommended", "enrolled", "in_progress", "completed", "certified", "dismissed"];

export default function LearningPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [courses, setCourses] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [courseSearch, setCourseSearch] = useState("");
  const [engineRunning, setEngineRunning] = useState(false);

  const isAdmin = ["admin", "hr_manager"].includes(user.role);

  const loadStats = useCallback(() => {
    api.get("/learning/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const loadAssignments = useCallback(() => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (deptFilter !== "all") params.department = deptFilter;
    api.get("/learning/assignments", { params }).then((r) => setAssignments(r.data.assignments)).catch(() => {});
  }, [statusFilter, deptFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);
  useEffect(() => {
    if (isAdmin) api.get("/employees/departments").then((r) => setDepartments(r.data.departments)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/learning/catalog", { params: courseSearch ? { search: courseSearch } : {} })
        .then((r) => setCourses(r.data.courses)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [courseSearch]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/learning/assignments/${id}`, { status });
      toast.success(`Assignment ${status.replace("_", " ")}`);
      loadAssignments();
      loadStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runEngine = async () => {
    setEngineRunning(true);
    try {
      const { data } = await api.post("/learning/run-engine");
      toast.success(`Engine run: ${data.assignments_created} new assignments, ${data.notifications_created} nudges`);
      loadAssignments();
      loadStats();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEngineRunning(false);
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="learning-page">
      {/* Funnel */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="learning-funnel">
        {FUNNEL_ORDER.map((s) => (
          <div key={s} className="bg-white border border-border p-4" data-testid={`funnel-${s}`}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{s.replace("_", " ")}</p>
            <p className="text-2xl font-bold text-[var(--brand-primary)] mt-1.5">{stats?.funnel?.[s] ?? 0}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="assignments">
        <div className="flex items-center justify-between">
          <TabsList className="rounded-none bg-white border border-border h-10 p-0">
            <TabsTrigger value="assignments" data-testid="tab-assignments"
              className="rounded-none h-full px-5 text-[12px] uppercase tracking-wider data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white">
              Assignments
            </TabsTrigger>
            <TabsTrigger value="catalog" data-testid="tab-catalog"
              className="rounded-none h-full px-5 text-[12px] uppercase tracking-wider data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white">
              Coursera Catalog
            </TabsTrigger>
          </TabsList>
          {isAdmin && (
            <Button onClick={runEngine} disabled={engineRunning} data-testid="learning-run-engine-btn"
              variant="outline" className="rounded-none h-9 text-[12px] border-teal-600/50 text-teal-800">
              <PlayCircle className={`h-4 w-4 ${engineRunning ? "animate-spin" : ""}`} />
              Run auto-assignment engine
            </Button>
          )}
        </div>

        <TabsContent value="assignments" className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="rounded-none h-9 w-44 text-[13px]" data-testid="assignments-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all" className="rounded-none text-[13px]">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} className="rounded-none text-[13px]">{s.replace("_", " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={deptFilter} onValueChange={setDeptFilter}>
                <SelectTrigger className="rounded-none h-9 w-52 text-[13px]" data-testid="assignments-dept-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all" className="rounded-none text-[13px]">All departments</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d} className="rounded-none text-[13px]">{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {assignments && (
              <span className="text-[12px] font-mono text-muted-foreground ml-auto" data-testid="assignments-count">
                {assignments.length} assignments
              </span>
            )}
          </div>

          {!assignments ? (
            <Skeleton className="h-72 w-full rounded-none" />
          ) : (
            <div className="bg-white border border-border">
              <Table data-testid="assignments-table">
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="text-[11px] uppercase tracking-[0.1em] pl-5">Employee</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-[0.1em]">Course</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-[0.1em]">Focus Skill</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-[0.1em]">Source / Target</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-[0.1em] text-right pr-5">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.map((a) => (
                    <TableRow key={a.id} className="border-border hover:bg-slate-50/70" data-testid={`assignment-row-${a.id}`}>
                      <TableCell className="pl-5">
                        <p className="text-[13px] font-medium text-slate-800">{a.employee_name}</p>
                        <p className="text-[11px] text-muted-foreground">{a.department}</p>
                      </TableCell>
                      <TableCell>
                        <a href={a.course_url} target="_blank" rel="noreferrer"
                          className="text-[13px] text-slate-800 hover:text-teal-700 inline-flex items-center gap-1">
                          {a.course_title.length > 48 ? a.course_title.slice(0, 48) + "…" : a.course_title}
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </a>
                        <p className="text-[11px] text-muted-foreground">{a.partner}</p>
                      </TableCell>
                      <TableCell className="text-[12px] text-slate-600">{a.focus_skill}</TableCell>
                      <TableCell>
                        <span className="text-[10px] font-mono uppercase text-slate-500">{a.source}</span>
                        {a.target_ref && (
                          <p className="text-[11px] text-muted-foreground truncate max-w-[220px]" title={a.target_ref}>
                            {a.target_ref}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right pr-5">
                        <Select value={a.status} onValueChange={(v) => changeStatus(a.id, v)}>
                          <SelectTrigger className="rounded-none h-8 w-36 text-[11px] font-mono uppercase ml-auto"
                            data-testid={`assignment-status-${a.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s} value={s} className="rounded-none text-[12px]">{s.replace("_", " ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="catalog" className="mt-4 space-y-4">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="catalog-search-input" placeholder="Search courses, partners, skills…"
              value={courseSearch} onChange={(e) => setCourseSearch(e.target.value)}
              className="rounded-none h-9 pl-9 text-[13px]" />
          </div>
          {!courses ? (
            <Skeleton className="h-72 w-full rounded-none" />
          ) : (
            <div className="bg-white border border-border">
              <Table data-testid="catalog-table">
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="text-[11px] uppercase tracking-[0.1em] pl-5">Course</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-[0.1em]">Skills</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-[0.1em]">Level</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-[0.1em] text-right">Duration</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-[0.1em] text-right pr-5">Certificate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {courses.map((c) => (
                    <TableRow key={c.id} className="border-border hover:bg-slate-50/70">
                      <TableCell className="pl-5">
                        <a href={c.url} target="_blank" rel="noreferrer" data-testid={`course-link-${c.id}`}
                          className="text-[13px] font-medium text-slate-800 hover:text-teal-700 inline-flex items-center gap-1">
                          {c.title}
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </a>
                        <p className="text-[11px] text-muted-foreground">{c.partner}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[260px]">
                          {c.skills.map((s) => (
                            <span key={s} className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 border border-border">{s}</span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-[12px] text-slate-600 capitalize">{c.level}</TableCell>
                      <TableCell className="text-right font-mono text-[12px] text-slate-600">{c.duration_weeks}w</TableCell>
                      <TableCell className="text-right pr-5">
                        {c.has_certification ? <Award className="h-4 w-4 text-teal-600 inline" /> : <span className="text-[11px] text-muted-foreground">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
